import React, { useMemo, useState } from 'react';

const avatarChoices = ['🤖', '🛡️', '⚡', '🌐', '💎', '🚀'];

const steps = [
  { key: 'profile', label: 'Profil' },
  { key: 'security', label: 'Schutz' },
  { key: 'backup', label: 'Backup' },
  { key: 'launch', label: 'Start' }
];

export default function FirstRunOnboarding({
  activeIdentity,
  displayGaiaID,
  profileDisplayName,
  profileAvatar,
  onProfileSave,
  onComplete,
  onSkip,
  onOpenInbox,
  onOpenChat,
  onOpenChannels,
  onOpenSecurity
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [draftName, setDraftName] = useState(profileDisplayName || activeIdentity?.DisplayName || activeIdentity?.displayName || '');
  const [draftAvatar, setDraftAvatar] = useState(profileAvatar || '🤖');
  const [backupConfirmed, setBackupConfirmed] = useState(false);

  const currentStep = steps[stepIndex];
  const identityLabel = useMemo(() => {
    if (!activeIdentity) return 'Gaia-ID wird geladen';
    return displayGaiaID(activeIdentity.GaiaID || activeIdentity.gaiaID || '');
  }, [activeIdentity, displayGaiaID]);

  const canContinue = currentStep.key !== 'backup' || backupConfirmed;

  const saveProfileAndContinue = () => {
    const cleanName = draftName.trim();
    onProfileSave({
      displayName: cleanName || profileDisplayName || activeIdentity?.DisplayName || activeIdentity?.displayName || 'GaiaCom User',
      avatar: draftAvatar
    });
    setStepIndex(1);
  };

  const goNext = () => {
    if (currentStep.key === 'profile') {
      saveProfileAndContinue();
      return;
    }
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }
    onComplete();
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  return (
    <div className="first-run-overlay" role="dialog" aria-modal="true" aria-labelledby="first-run-title">
      <section className="first-run-shell">
        <div className="first-run-progress" aria-label="Onboarding Fortschritt">
          {steps.map((step, index) => (
            <button
              key={step.key}
              type="button"
              className={`first-run-step ${index === stepIndex ? 'active' : ''} ${index < stepIndex ? 'done' : ''}`}
              onClick={() => {
                if (index <= stepIndex) setStepIndex(index);
              }}
            >
              <span>{index + 1}</span>
              <strong>{step.label}</strong>
            </button>
          ))}
        </div>

        <div className="first-run-content">
          <header className="first-run-header">
            <span className="first-run-kicker">GaiaCom Initial Setup</span>
            <h2 id="first-run-title">
              {currentStep.key === 'profile' && 'Mach GaiaCom zu deinem Raum.'}
              {currentStep.key === 'security' && 'Was GaiaCom gerade fuer dich schuetzt.'}
              {currentStep.key === 'backup' && 'Ein Backup ist dein echter Notausgang.'}
              {currentStep.key === 'launch' && 'Bereit. Was willst du zuerst tun?'}
            </h2>
            <p>
              {currentStep.key === 'profile' && 'Dein Anzeigename und Avatar bleiben lokal steuerbar und koennen spaeter jederzeit im Profil geaendert werden.'}
              {currentStep.key === 'security' && 'GaiaCom trennt Identitaet, Schluessel, Nachrichten und Netzwerkstatus. Du siehst hier nur die kurze Nutzerfassung.'}
              {currentStep.key === 'backup' && 'Ohne Wiederherstellungsdaten kann niemand deine Identitaet retten. Genau das ist der Punkt von echter Ende-zu-Ende-Sicherheit.'}
              {currentStep.key === 'launch' && 'Du kannst direkt loslegen oder den Rundgang beenden und im Dashboard weiterarbeiten.'}
            </p>
          </header>

          {currentStep.key === 'profile' && (
            <div className="first-run-profile-grid">
              <div className="first-run-avatar-preview" aria-hidden="true">
                {draftAvatar.startsWith('data:image/') ? <img src={draftAvatar} alt="" /> : <span>{draftAvatar}</span>}
              </div>
              <div className="first-run-form">
                <label htmlFor="first-run-display-name">Anzeigename</label>
                <input
                  id="first-run-display-name"
                  type="text"
                  value={draftName}
                  maxLength={42}
                  onChange={(event) => setDraftName(event.target.value)}
                  placeholder="Wie soll GaiaCom dich anzeigen?"
                />
                <div className="first-run-avatar-list" aria-label="Avatar auswaehlen">
                  {avatarChoices.map((avatar) => (
                    <button
                      key={avatar}
                      type="button"
                      className={`first-run-avatar-choice ${draftAvatar === avatar ? 'active' : ''}`}
                      onClick={() => setDraftAvatar(avatar)}
                      aria-label={`Avatar ${avatar}`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
                <code>{identityLabel}</code>
              </div>
            </div>
          )}

          {currentStep.key === 'security' && (
            <div className="first-run-info-grid">
              <article>
                <strong>Identitaet</strong>
                <span>Deine Gaia-ID ist dein Netzwerkanker. Kontakte koennen Schluesselwechsel erkennen.</span>
              </article>
              <article>
                <strong>Nachrichten</strong>
                <span>Inhalte werden lokal entschluesselt. Bearbeitete Nachrichten bekommen neue Signaturen.</span>
              </article>
              <article>
                <strong>Netzwerk</strong>
                <span>Security Center und Abuse Center machen Risiken sichtbar, ohne globale Hintertuer.</span>
              </article>
            </div>
          )}

          {currentStep.key === 'backup' && (
            <div className="first-run-backup-panel">
              <div>
                <strong>Wichtig fuer normale Nutzer</strong>
                <p>Speichere deine Wiederherstellungsdaten getrennt von diesem Geraet. Bei Verlust von Passwort und Backup bleibt GaiaCom absichtlich verschlossen.</p>
              </div>
              <label className="first-run-check">
                <input
                  type="checkbox"
                  checked={backupConfirmed}
                  onChange={(event) => setBackupConfirmed(event.target.checked)}
                />
                <span>Ich weiss, dass mein Backup fuer Wiederherstellung und Geraetewechsel entscheidend ist.</span>
              </label>
            </div>
          )}

          {currentStep.key === 'launch' && (
            <div className="first-run-action-grid">
              <button type="button" onClick={onOpenInbox}>
                <strong>Posteingang</strong>
                <span>Mails lesen und erste Nachricht verfassen.</span>
              </button>
              <button type="button" onClick={onOpenChat}>
                <strong>Kontakte</strong>
                <span>Ersten Kontakt pruefen oder Chat starten.</span>
              </button>
              <button type="button" onClick={onOpenChannels}>
                <strong>Channels</strong>
                <span>Oeffentliche Frequenzen entdecken.</span>
              </button>
              <button type="button" onClick={onOpenSecurity}>
                <strong>Security Center</strong>
                <span>GaiaShield Status ansehen.</span>
              </button>
            </div>
          )}
        </div>

        <footer className="first-run-footer">
          <button type="button" className="first-run-skip-btn" onClick={onSkip}>
            Spaeter
          </button>
          <div>
            {stepIndex > 0 && (
              <button type="button" className="btn-secondary" onClick={goBack}>
                Zurueck
              </button>
            )}
            <button type="button" className="btn-primary" onClick={goNext} disabled={!canContinue}>
              {stepIndex === steps.length - 1 ? 'Setup abschliessen' : 'Weiter'}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
